import React, { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Table,
  Button,
  Form,
  Input,
  Space,
  notification,
  Modal,
  InputNumber,
  Upload,
  Flex,
  Spin,
} from "antd";
import {
  UploadOutlined,
  DownloadOutlined,
  PlusOutlined,
  LoadingOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useTypedSelector } from "../../store";
import { redirect } from "react-router-dom";
import {
  createBranchRequest,
  getBranchRequest,
  resetBranchMessage,
  resetBranchSuccess,
  updateBranchRequest,
  uploadBranchRequest,
} from "../../slices/branch";
import { UploadFile, UploadFileStatus, RcFile } from "antd/es/upload/interface";

const Branch: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [isAddBranchActive, setIsAddBranchActive] = useState(false);
  const [isDetailActive, setIsDetailActive] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [detail, setDetail] = useState<any>(null);
  const [isModal, setIsModal] = useState<boolean>(false);
  const [isModalEdit, setIsModalEdit] = useState<boolean>(false);
  const [isModalUpload, setIsModalUpload] = useState<boolean>(false);
  const [payloadPost, setPayloadPost] = useState<any>(null);
  const [payloadUpdate, setPayloadUpdate] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [fileList, setFileList] = useState<UploadFile<any>[]>([]);

  const {
    isLoading,
    branchLists,
    isLogin,
    isCreate,
    isSuccess,
    message,
    isUpdate,
    isUpload,
  } = useTypedSelector((state) => {
    return {
      isLoading: state.Branch.pageIsLoading,
      branchLists: state.Branch.branchList,
      isLogin: state.Auth.isLogin,
      isCreate: state.Branch.createBranch,
      isSuccess: state.Branch.isSuccess,
      message: state.Branch.message,
      isUpdate: state.Branch.updateBranch,
      isUpload: state.Branch.uploadBranch,
    };
  });

  const filteredData = branchLists?.filter(
    (branch) =>
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.type.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const [form] = Form.useForm();

  useEffect(() => {
    dispatch(resetBranchMessage());
    if (!isLogin) {
      navigate("/login");
    } else {
      dispatch(getBranchRequest() as any);
    }
  }, [isLogin, dispatch]);

  useEffect(() => {
    dispatch(resetBranchMessage());
    dispatch(getBranchRequest() as any);
  }, [isCreate, isUpdate, isUpload]);

  useEffect(() => {
    if (selectedBranch) {
      setDetail(selectedBranch);
    }
  }, [selectedBranch]);

  useEffect(() => {
    if (selectedBranch) {
      form.setFieldsValue(selectedBranch);
    }
  }, [selectedBranch, form]);

  const handleAddBranch = () => {
    setIsAddBranchActive(true);
    setIsDetailActive(false);
  };

  const handleDetailClick = (record: any) => {
    setSelectedBranch(record);
    setIsDetailActive(true);
    setIsAddBranchActive(false);
  };

  const handleSaveBranch = (values: any) => {
    setPayloadPost(values);
    setIsModal(true);
  };

  const handleEditBranch = (values: any) => {
    setPayloadUpdate({ ...values, id: selectedBranch._id });
    setIsModalEdit(true);
  };

  const handleCancel = () => {
    setIsAddBranchActive(false);
    setIsDetailActive(false);
  };

  useEffect(() => {
    if (isSuccess) {
      notification.success({
        message: "Branch added, updated or uploaded successfully!",
        onClose: () => {
          dispatch(resetBranchSuccess());
          setIsAddBranchActive(false);
          setIsDetailActive(false);
          setIsModalUpload(false);
          setFileList([]);
        },
      });
    }

    if (message.length) {
      notification.error({ message });
      dispatch(resetBranchMessage());
    }
  }, [isSuccess, message, dispatch, isUpdate, isUpload]);

  const handleOk = () => {
    setIsModal(false);
    dispatch(createBranchRequest(payloadPost) as any);
  };

  const handleOkEdit = () => {
    setIsModalEdit(false);
    dispatch(updateBranchRequest(payloadUpdate) as any);
  };

  const handleExportToExcel = () => {
    console.log("FD", filteredData);

    const formattedData = filteredData.map((item) => ({
      _id: item._id || null,
      address: item.address || null,
      codeBranch: item.codeBranch || null,
      name: item.name || null,
      attendanceRange: item.attendanceRange || null,
      Location: item.location?.coordinates
        ? `${item.location.coordinates[0]}, ${item.location.coordinates[1]}`
        : "0, 0", // Format koordinat jadi string "latitude, longitude"
      Status: item.isActive === true ? "Active" : "Inactive", // Format status jadi teks
      approvalLine: item.approvalLine || null,
      createdAt: item.createdAt || null,
      isDeleted: item.isDeleted || null,
      media: item.media || null,
      statusProcess: item.statusProcess || null,
      type: item.type || null,
    }));

    // const ws = XLSX.utils.json_to_sheet(filteredData);
    const ws = XLSX.utils.json_to_sheet(formattedData);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "branch_master");
    XLSX.writeFile(wb, "branch.xlsx");
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a: any, b: any) => a.name.localeCompare(b.name),
      render: (text: string, record: any) => (
        <a onClick={() => handleDetailClick(record)}>{text}</a>
      ),
    },
    { title: "Type", dataIndex: "type", key: "type" },
    { title: "Code Branch", dataIndex: "codeBranch", key: "codeBranch" },
    { title: "Address", dataIndex: "address", key: "address" },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      render: (location: { coordinates: number[] }) =>
        `${location.coordinates[1]}, ${location.coordinates[0]}`,
    },
    {
      title: "Flag Active",
      dataIndex: "isActive",
      key: "isActive",
      render: (flag: boolean) => (flag ? "Active" : "Inactive"),
    },
    {
      title: "Attendance Range",
      dataIndex: "attendanceRange",
      key: "attendanceRange",
      sorter: (a: any, b: any) => a.attendanceRange - b.attendanceRange,
    },
    {
      title: "Status Process",
      dataIndex: "statusProcess",
      key: "statusProcess",
    },
  ];

  const handleUpload = () => {
    if (fileList.length === 0) {
      notification.warning({
        message: "Please select a file before uploading.",
      });
      return;
    }

    const formData = new FormData();
    fileList.forEach((file) => {
      if (file.originFileObj) {
        formData.append("data", file.originFileObj as File);
        dispatch(uploadBranchRequest(formData) as any);
      }
    });
  };

  const uploadProps = {
    onRemove: (file: UploadFile<any>) => {
      setFileList((prevFileList) =>
        prevFileList.filter((f) => f.uid !== file.uid)
      );
    },
    beforeUpload: (file: File & RcFile) => {
      const uploadFile: UploadFile<any> = {
        uid: file.uid || file.name, // Gunakan UID atau nama file
        name: file.name,
        status: "done" as UploadFileStatus,
        originFileObj: file,
      };
      setFileList([uploadFile]);
      return false; // Prevent automatic upload
    },
    fileList,
  };

  const downloadBranchTemplate = () => {
    const headers = [
      "codeBranch",
      "name",
      "address",
      "latitude",
      "longitude",
      "flagactive",
      "approvalLine",
    ];
    const data = [
      [
        "BR001",
        "Branch name 1",
        "123 Main St, City",
        "-74.0060",
        "40.7128",
        1,
        "ID employee exp: (DB0001)",
      ],
    ];

    // Create a worksheet with the headers and sample data
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Branch Template");

    // Write the workbook to a file and trigger the download
    XLSX.writeFile(workbook, "branch_template.xlsx");
  };

  return (
    <div>
      <Space style={{ marginBottom: 30 }}>
        <Button
          icon={<UploadOutlined />}
          style={{ backgroundColor: "#57a773", color: "white" }}
          onClick={() => setIsModalUpload(true)}
        >
          Upload Branch
        </Button>

        <Button
          icon={<DownloadOutlined />}
          style={{ backgroundColor: "#57a773", color: "white" }}
          onClick={() => downloadBranchTemplate()}
        >
          Download Template Excel
        </Button>
        <Button
          type="primary"
          icon={<FileExcelOutlined />}
          style={{ backgroundColor: "#00a65a", borderColor: "#00a65a" }}
          onClick={handleExportToExcel}
        >
          Export to Excel
        </Button>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddBranch}
        >
          Add Branch
        </Button>
        <Input
          placeholder="Search by name or type"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </Space>

      {/* Table */}
      {!isAddBranchActive && !isDetailActive && (
        <Table
          dataSource={
            filteredData && filteredData.length > 0 ? filteredData : []
          }
          columns={columns}
          scroll={{ x: "max-content" }}
          pagination={{ pageSize: 10 }}
        />
      )}

      {/* Add Branch Form */}
      {isAddBranchActive && (
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "20px",
            border: "1px solid #ddd",
            background: "#fff",
          }}
        >
          <h3>Add Branch</h3>
          <Form layout="vertical" onFinish={handleSaveBranch}>
            <Form.Item
              label="Name"
              name="name"
              rules={[
                { required: true, message: "Please input the branch name!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Type"
              name="type"
              rules={[
                { required: true, message: "Please input the branch type!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Address"
              name="address"
              rules={[{ required: true, message: "Please input the address!" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Code Branch"
              name="codeBranch"
              rules={[
                { required: true, message: "Please input the code branch!" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Attendance Range"
              name="attendanceRange"
              rules={[
                {
                  required: true,
                  message: "Please input the attendance range!",
                },
              ]}
            >
              <InputNumber placeholder="50" style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              label="Latitude"
              name="latitude"
              rules={[
                { required: true, message: "Please input the latitude!" },
              ]}
            >
              <InputNumber
                placeholder="-6.1010101"
                min={-90} // Min value for latitude
                max={90} // Max value for latitude
                step={0.0000001} // Step for latitude precision
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label="Longitude"
              name="longitude"
              rules={[
                { required: true, message: "Please input the longitude!" },
              ]}
            >
              <InputNumber
                placeholder="106.1010101"
                min={-180} // Min value for longitude
                max={180} // Max value for longitude
                step={0.0000001} // Step for longitude precision
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item
              label="Approval Line"
              name="approvalLine"
              rules={[
                {
                  required: true,
                  message: "Please input the branch Approval Line!",
                },
              ]}
            >
              <Input placeholder="exp: DB0001" />
            </Form.Item>
            <Button type="primary" htmlType="submit">
              Save
            </Button>
            <Button onClick={handleCancel} style={{ marginLeft: 10 }}>
              Cancel
            </Button>
          </Form>
        </div>
      )}

      {/* Detail Branch Form */}
      {isDetailActive && selectedBranch && (
        <div
          style={{
            maxWidth: "600px",
            margin: "0 auto",
            padding: "20px",
            border: "1px solid #ddd",
            background: "#fff",
          }}
        >
          <h3>Branch Detail</h3>
          <Form
            layout="vertical"
            form={form}
            initialValues={{
              ...selectedBranch, // Nilai awal diambil dari selectedBranch
              latitude: selectedBranch?.location?.coordinates?.[1],
              longitude: selectedBranch?.location?.coordinates?.[0],
            }}
            onFinish={handleEditBranch}
          >
            <Form.Item label="Name" name="name">
              <Input />
            </Form.Item>
            <Form.Item label="Type" name="type">
              <Input />
            </Form.Item>
            <Form.Item label="Address" name="address">
              <Input />
            </Form.Item>
            <Form.Item label="Code Branch" name="codeBranch">
              <Input />
            </Form.Item>
            {/* <Form.Item
              label="Attendance Range"
              name="attendanceRange"
              rules={[
                {
                  required: true,
                  message: "Please input the attendance range!",
                },
              ]}
            >
              <InputNumber
                placeholder="50"
                style={{ width: "100%" }}
                value={selectedBranch.attendanceRange}
              />
              <>{selectedBranch?.location?.coordinates?.[1]}</>
            </Form.Item>
            {selectedBranch?.location?.coordinates ? (
              <Form.Item label="Latitude" name="latitude">
                <InputNumber
                  placeholder={selectedBranch?.location?.coordinates?.[1]}
                  // min={-90} // Min value for latitude
                  // max={90} // Max value for latitude
                  // step={0.0000001} // Step for latitude precision
                  style={{ width: "100%" }}
                  value={`${selectedBranch?.location?.coordinates?.[1]}` || 0}
                />
              </Form.Item>
            ) : (
              <>Loading...</>
            )}
            <Form.Item label="Longitude" name="longitude">
              <InputNumber
                placeholder={selectedBranch?.location?.coordinates?.[0]}
                min={-180} // Min value for longitude
                max={180} // Max value for longitude
                step={0.0000001} // Step for longitude precision
                style={{ width: "100%" }}
                value={selectedBranch.location.coordinates[0]}
              />
            </Form.Item> */}
            <Form.Item
              label="Attendance Range"
              name="attendanceRange"
              rules={[
                {
                  required: true,
                  message: "Please input the attendance range!",
                },
              ]}
            >
              <InputNumber placeholder="50" style={{ width: "100%" }} />
            </Form.Item>

            {selectedBranch?.location?.coordinates ? (
              <>
                <Form.Item label="Latitude" name="latitude">
                  <InputNumber
                    placeholder={selectedBranch.location.coordinates[1]}
                    min={-90}
                    max={90}
                    step={0.0000001}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
                <Form.Item label="Longitude" name="longitude">
                  <InputNumber
                    placeholder={selectedBranch.location.coordinates[0]}
                    min={-180}
                    max={180}
                    step={0.0000001}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </>
            ) : (
              <>Loading...</>
            )}

            {/* <Form.Item label="Status Process" name="statusProcess">
              <Input />
            </Form.Item> */}
            <Form.Item label="Approval Line" name="approvalLine">
              <Input />
            </Form.Item>
            <Button onClick={handleCancel}>Close</Button>
            <Button type="primary" htmlType="submit" style={{ marginLeft: 10 }}>
              Edit
            </Button>
          </Form>
        </div>
      )}

      <Modal
        title="Submit Confirm"
        open={isModal}
        onCancel={() => {
          // setEmployeeId("");
          setIsModal(false);
        }}
        onOk={handleOk}
      >
        <p>Create New Branch</p>
        <p>Are you sure?</p>
      </Modal>

      <Modal
        title="Submit Confirm"
        open={isModalEdit}
        onCancel={() => {
          // setEmployeeId("");
          setIsModalEdit(false);
        }}
        onOk={handleOkEdit}
      >
        <p>Update Branch</p>
        <p>Are you sure?</p>
      </Modal>

      {/* modal upload */}
      <Modal
        title="Upload Branch Confirm"
        open={isModalUpload}
        footer={null}
        onCancel={() => {
          setIsModalUpload(false);
          setFileList([]);
        }}
        bodyStyle={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "300px", // Sesuaikan tinggi modal
        }}
      >
        <Upload {...uploadProps} accept=".xlsx,.xls">
          <Button
            icon={<UploadOutlined />}
            style={{ width: 300, marginBottom: 20 }}
          >
            Select File
          </Button>
        </Upload>
        <Button
          type="primary"
          onClick={handleUpload}
          style={{
            backgroundColor: "#217346", // Warna hijau khas Excel
            borderColor: "#217346", // Sesuaikan border dengan warna latar belakang
            color: "#fff",
            width: 300, // Warna teks putih
          }}
        >
          {!isLoading && <>Upload Branch</>}
          {isLoading && (
            <>
              <Flex align="center" gap="middle">
                <Spin indicator={<LoadingOutlined spin />} size="small" />
              </Flex>
            </>
          )}
        </Button>
      </Modal>
    </div>
  );
};

export default Branch;
